import { memo, useEffect, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconTrashOutline18 as Trash } from "nucleo-ui-outline-18"
import { z } from "zod"

import { isValidDomain } from "@productlane/lib/src/domain"
import {
	Button,
	Dialog,
	Form,
	FormInput,
	Input,
	Tooltip,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { mutateOk, useZero } from "@/lib/zero"
import { findCompanyByDomain, findCompanyByName } from "./customer-domains"
import { cleanExternalIds, externalIdsChanged } from "./external-ids"
import { ExternalIdsField } from "./external-ids-field"

interface UpdateCustomerDialogProps {
	values: CreateCustomerFormValues
	isOpen: boolean
	onOpenChange: (value: boolean) => void
}

const UpdateCustomerFormSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	domains: z.array(z.string().min(1)).default([]),
})
type CreateCustomerFormValues = z.infer<typeof UpdateCustomerFormSchema>

export const UpdateCustomerDialog = memo(
	({ values, onOpenChange, isOpen }: UpdateCustomerDialogProps) => {
		const form = useZodForm({
			schema: UpdateCustomerFormSchema,
			defaultValues: values,
		})
		const zero = useZero()

		const [companies] = useQuery(queries.companies({}), { enabled: isOpen })

		const utils = api.useUtils()
		const savedExternalIds = api.company.externalIds.useQuery(
			{ id: values.id },
			{ enabled: isOpen },
		)
		const setExternalIds = api.company.setExternalIds.useMutation()
		const [externalIds, setExternalIdsDraft] = useState<string[]>([])
		useEffect(() => {
			if (isOpen && savedExternalIds.data)
				setExternalIdsDraft(savedExternalIds.data)
		}, [isOpen, savedExternalIds.data])

		const domains = form.watch("domains")

		const addDomain = () => {
			form.setValue("domains", [...domains, ""])
		}

		const removeDomain = (index: number) => {
			const newDomains = domains.filter((_, i) => i !== index)
			form.setValue("domains", newDomains)
		}

		const updateDomain = (index: number, value: string) => {
			const newDomains = [...domains]
			newDomains[index] = value
			form.setValue("domains", newDomains)
		}

		const isDomainValid = (domain: string) => {
			if (!domain.trim()) return true
			return isValidDomain(domain)
		}

		useEffect(() => {
			form.reset(values)
		}, [values, form])

		return (
			<Dialog
				isOpen={isOpen}
				title="Update Customer"
				onOpenChange={onOpenChange}
				footer={
					<Button form="update-company-form" type="submit">
						Submit
					</Button>
				}
			>
				<Form
					id="update-company-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						const invalidDomains = values.domains
							.map((domain) => domain.trim())
							.filter((domain) => domain && !isValidDomain(domain))
						if (invalidDomains.length > 0) {
							toast.error({
								title: `Enter a domain like "acme.com"`,
								description: `These are not valid domains: ${invalidDomains.join(", ")}`,
							})
							return
						}

						const cleanDomains = Array.from(
							new Set(
								values.domains
									.map((domain) => domain.trim().toLowerCase())
									.filter(Boolean),
							),
						)

						const takenDomain = cleanDomains.find((domain) =>
							findCompanyByDomain(companies, domain, values.id),
						)
						if (takenDomain) {
							const owner = findCompanyByDomain(
								companies,
								takenDomain,
								values.id,
							)
							toast.error({
								title: `"${takenDomain}" already belongs to ${owner?.name ?? "another customer"}`,
								description: "Remove it here or from that customer first.",
							})
							return
						}

						if (findCompanyByName(companies, values.name, values.id)) {
							toast.error({
								title: `A customer named "${values.name.trim()}" already exists`,
								description: "Pick a different name.",
							})
							return
						}

						await mutateOk(
							zero.mutate(
								mutators.companies.update({
									id: values.id,
									name: values.name,
									domains: cleanDomains,
								}),
							),
						)

						const saved = savedExternalIds.data
						if (saved && externalIdsChanged(saved, externalIds)) {
							try {
								const next = await setExternalIds.mutateAsync({
									id: values.id,
									externalIds: cleanExternalIds(externalIds),
								})
								utils.company.externalIds.setData({ id: values.id }, next)
							} catch (error) {
								toast.error({
									title: "External IDs were not saved",
									description:
										error instanceof Error ? error.message : undefined,
								})
								return
							}
						}
						onOpenChange(false)
					}}
				>
					<FormInput name="name" label="Name" />

					<div className="space-y-2">
						<div className="flex flex-row items-center justify-between text-sm font-medium">
							<div>Domains</div>
							<Button variant="ghost" size="sm" onClick={addDomain}>
								Add domain
							</Button>
						</div>
						{domains.length === 0 ? (
							<p className="text-muted-foreground text-sm">
								No domains added yet
							</p>
						) : (
							<div className="space-y-2">
								{domains.map((domain, index) => {
									const isValid = isDomainValid(domain)
									return (
										<div key={index} className="space-y-1">
											<div className="flex gap-2">
												<Input
													name="domain"
													type="text"
													value={domain}
													onChange={(e) => updateDomain(index, e.target.value)}
													placeholder="example.com"
													className={
														!isValid
															? "border-red-500 focus-visible:ring-red-500"
															: "border-input focus-visible:ring-ring"
													}
												/>
												<Tooltip label="Remove domain" side="bottom">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeDomain(index)}
														className="px-2"
													>
														<Trash className="h-4 w-4" />
													</Button>
												</Tooltip>
											</div>
											{!isValid && domain.trim() && (
												<p className="text-xs text-red-500">
													Enter a domain like &quot;acme.com&quot;.
												</p>
											)}
										</div>
									)
								})}
							</div>
						)}
					</div>

					<ExternalIdsField
						value={externalIds}
						onChange={setExternalIdsDraft}
						isLoading={savedExternalIds.isLoading}
					/>
				</Form>
			</Dialog>
		)
	},
)
